import React, {useMemo} from 'react'
import styles from './DisplayTabContent.module.scss'
import clsx from 'clsx'
import {StorageWrapper} from '../../../utils/storage'
import {TranslationObject} from '@colonist/i18n'
import {t, T} from '@colonist/ui'
import IconArrow from '@static/images/icon_arrow.svg'
import {useGameStore} from '../../../store/GameStoreContext'

const DisplayInputField = ({
    label,
    value,
    options,
    onChange,
}: {
    label: TranslationObject
    value: string | number | boolean
    options: {
        value: string | number | boolean
        label: TranslationObject
    }[]
    onChange: (value: string | number | boolean) => void
}) => {
    const selectedIndex = useMemo(() => {
        const idx = options.findIndex((option) => option.value === value)
        return idx >= 0 ? idx : 0
    }, [options, value])

    const selectedValue = useMemo(() => options[selectedIndex], [options, selectedIndex])

    const handleBackClick = () => {
        if(selectedIndex > 0) {
            onChange(options[selectedIndex - 1].value)
        }
    }

    const handleNextClick = () => {
        if(selectedIndex < options.length - 1) {
            onChange(options[selectedIndex + 1].value)
        }
    }

    return (
        <>
            <div className={styles.label}><T id={label} /></div>
            <div className={styles.fieldWrapper}>
                <img
                    alt={t('strings:imgAlts.IconArrowPrevious')}
                    className={clsx(styles.backButton, {[styles.disabled]: selectedIndex === 0})}
                    onClick={handleBackClick}
                    src={IconArrow} />
                <div className={styles.fieldValue}><T id={selectedValue.label} /></div>
                <img
                    alt={t('strings:imgAlts.IconArrowNext')}
                    className={clsx(styles.nextButton, {[styles.disabled]: selectedIndex === options.length - 1})}
                    onClick={handleNextClick}
                    src={IconArrow} />
            </div>
        </>
    )
}

export const DisplayTabContent = () => {
    const setCanvasOptions = useGameStore((state) => state.gameUserSettings.setCanvasOptions)
    const isAnimationsEnabled = useGameStore((state) => state.gameUserSettings.isAnimationsEnabled)
    const setIsAnimationsEnabled = useGameStore((state) => state.gameUserSettings.setIsAnimationsEnabled)
    const isKeyboardShortcutsEnabled = useGameStore((state) => state.gameUserSettings.isKeyboardShortcutsEnabled)
    const setIsKeyboardShortcutsEnabled = useGameStore((state) => state.gameUserSettings.setIsKeyboardShortcutsEnabled)
    const isAntialiasEnabled = useGameStore((state) => state.gameUserSettings.isAntialiasEnabled)
    const setIsAntialiasEnabled = useGameStore((state) => state.gameUserSettings.setIsAntialiasEnabled)
    const resolution = useGameStore((state) => state.gameUserSettings.resolution)
    const setResolution = useGameStore((state) => state.gameUserSettings.setResolution)
    const powerPreference = useGameStore((state) => state.gameUserSettings.powerPreference)
    const setPowerPreference = useGameStore((state) => state.gameUserSettings.setPowerPreference)

    const handleChangeAnimationStatus = (value: boolean) => {
        setIsAnimationsEnabled(value)
    }

    const handleChangeKeyboardShortcuts = (value: boolean) => {
        setIsKeyboardShortcutsEnabled(value)
    }

    const handleChangeAntialias = (value: boolean) => {
        setCanvasOptions({antialias: value})
        setIsAntialiasEnabled(value)
    }

    const handleChangeResolution = (value: number) => {
        setCanvasOptions({resolution: value})
        setResolution(value)
    }

    const handleChangePowerPreference = (value: StorageWrapper.PowerPreference) => {
        setCanvasOptions({powerPreference: value})
        setPowerPreference(value)
    }

    return (
        <div className={styles.container}>
            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.animation.title'}}
                onChange={handleChangeAnimationStatus}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.animation.options.Inactive'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.animation.options.Active'}},
                ]}
                value={isAnimationsEnabled}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.keyboardShortcuts.title'}}
                onChange={handleChangeKeyboardShortcuts}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.keyboardShortcuts.options.Off'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.keyboardShortcuts.options.On'}},
                ]}
                value={isKeyboardShortcutsEnabled}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.antialias.title'}}
                onChange={handleChangeAntialias}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.antialias.options.Inactive'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.antialias.options.Active'}},
                ]}
                value={isAntialiasEnabled}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.resolution.title'}}
                onChange={handleChangeResolution}
                options={[
                    {value: 0.5, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 0.5}}},
                    {value: 1, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 1}}},
                    {value: 1.5, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 1.5}}},
                    {value: 2, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 2}}},
                ]}
                value={resolution}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.power.title'}}
                onChange={handleChangePowerPreference}
                options={[
                    {
                        value: StorageWrapper.PowerPreference.Low,
                        label: {key: 'strings:game.settings.displayTab.power.options.LowPower'}},
                    {
                        value: StorageWrapper.PowerPreference.Default,
                        label: {key: 'strings:game.settings.displayTab.power.options.Default'}},
                    {
                        value: StorageWrapper.PowerPreference.High, label:
                        {key: 'strings:game.settings.displayTab.power.options.HighPerformance'}},
                ]}
                value={powerPreference}
            />
        </div>
    )
}
